import React, { useState, useReducer, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { I18n } from "@aws-amplify/core";
import dayjs from "dayjs";
import { post } from "aws-amplify/api";
import currentUserToken from "CurrentUserToken";
import { APIStatusDialog } from "./APIStatusDialog";

function AddNotePatterns({ open, onClose, onSave, languages }) {
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });

  // Stato per i campi della lingua
  const [languageFields, setLanguageFields] = useState({});
  const [languageErrors, setLanguageErrors] = useState({});

  // Reset dei campi delle lingue quando il dialogo è aperto
  useEffect(() => {
    if (open) {
      const initialFields = {};
      languages.forEach((language) => {
        initialFields[language] = "";
      });
      setLanguageFields(initialFields);
      setLanguageErrors({});
    }
  }, [open, languages]); // Aggiungi 'languages' come dipendenza se necessario

  const AddNewType = async () => {
    try {
      if (!languageFields.ENGLISH) {
        throw new Error(I18n.get("English description are required")); // Traduzione
      }
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Adding Note Pattern"),
      });

      const requestBody = {};
      Object.keys(languageFields).forEach((language) => {
        if (languageFields[language].trim()) {
          requestBody[language] = languageFields[language].trim();
        }
      });

      const restOperation = post({
        apiName: "TelemonitoringAPI",
        path: "/pattern/type/new",
        options: {
          body: requestBody,
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            pattern_type: "NOTE",
            name: languageFields.ENGLISH,
          },
        },
      });

      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Note Pattern Added"),
        });
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Operation Failed"),
        });
      }
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
        onClose();
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Operation Failed"),
      });
      onClose();
    }
  };

  const formatLanguageName = (language) => {
    return I18n.get(
      language.charAt(0).toUpperCase() + language.slice(1).toLowerCase()
    );
  };

  const handleSave = async () => {
    const englishValue = languageFields.ENGLISH || "";
    const englishError = englishValue === "";

    if (englishError) {
      setLanguageErrors({
        ...languageErrors,
        ENGLISH: englishError,
      });
    } else {
      await AddNewType(); // Call AddNewType to save the new alarm type
      onSave({ ...languageFields }); // Pass the data back to the parent component
    }
  };

  const handleLanguageChange = (language, event) => {
    const value = event.target.value;
    setLanguageFields((prevFields) => ({
      ...prevFields,
      [language]: value,
    }));

    if (language === "ENGLISH" && value !== "") {
      setLanguageErrors((prevErrors) => ({
        ...prevErrors,
        ENGLISH: false,
      }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{I18n.get("Add New Note Pattern")}</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 2 }}>
          {languages.map((language) => (
            <TextField
              key={language}
              required={language === "ENGLISH"}
              fullWidth
              label={formatLanguageName(language)} // Traduzione dei nomi delle lingue
              value={languageFields[language]}
              onChange={(event) => handleLanguageChange(language, event)}
              error={language === "ENGLISH" && languageErrors.ENGLISH}
              helperText={
                language === "ENGLISH" && languageErrors.ENGLISH
                  ? I18n.get("English text is required") // Traduzione
                  : ""
              }
              sx={{ mb: 2 }}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {I18n.get("Cancel")} {/* Traduzione */}
        </Button>
        <Button onClick={handleSave} color="primary">
          {I18n.get("Save")} {/* Traduzione */}
        </Button>
      </DialogActions>
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
        onClose={() =>
          setApiStatus((prevState) => ({ ...prevState, open: false }))
        }
      />
    </Dialog>
  );
}

export default AddNotePatterns;
